/**
 * Data layer for `bigcommerce-integration-via-webhook`.
 *
 * Backend contract (see apps/api/src/routes/bigcommerce-integration-via-webhook.ts):
 *   GET  /api/v1/bigcommerce-integration-via-webhook/events  -> { count, events: WebhookEvent[] }
 *   POST /api/v1/bigcommerce-integration-via-webhook         -> (partner-only ingress; HMAC-verified)
 *
 * The secret-rotation endpoint is not yet wired server-side, so `rotateSecret()`
 * generates a cryptographically random hex string locally. This matches the
 * webhook-receiver shape (reveal-once) and keeps all ceremony out of the UI layer.
 *
 * Raw `fetch()` is confined to this file per the dash contract (D6).
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WebhookEvent {
	id: string;
	tenant_id: string;
	event_id: string;
	/** event_type is parsed from the stored payload_json when available. */
	event_type?: string | null;
	sig_valid: number;
	accepted: number;
	received_at: number;
	payload_json?: string;
}

export interface ListEventsResponse {
	count: number;
	events: WebhookEvent[];
}

export interface SubscribableEvent {
	key: string;
	label: string;
}

export interface RotateSecretResult {
	secret: string;
	rotated_at: number;
}

// ---------------------------------------------------------------------------
// Raw server response (what the BE actually sends)
// ---------------------------------------------------------------------------

interface RawWebhookRow {
	id: string;
	tenant_id: string;
	event_id: string;
	payload_json: string;
	sig_valid: number;
	accepted: number;
	received_at: number;
}

interface RawListResponse {
	count: number;
	events: RawWebhookRow[];
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const BASE = "/api/v1/bigcommerce-integration-via-webhook";

export function getEndpointUrl(): string {
	if (typeof window !== "undefined" && window.location) {
		return `${window.location.origin}${BASE}`;
	}
	return BASE;
}

// ---------------------------------------------------------------------------
// Response parsing
// ---------------------------------------------------------------------------

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

function eventTypeFromPayload(payloadJson: string | undefined): string | null {
	if (!payloadJson) return null;
	try {
		const parsed = JSON.parse(payloadJson);
		if (parsed && typeof parsed === "object") {
			const obj = parsed as Record<string, unknown>;
			for (const key of ["event_type", "scope", "event", "type"]) {
				const v = obj[key];
				if (typeof v === "string" && v.length > 0) return v;
			}
		}
	} catch {
		return null;
	}
	return null;
}

function normalizeEvent(row: RawWebhookRow): WebhookEvent {
	return {
		id: row.id,
		tenant_id: row.tenant_id,
		event_id: row.event_id,
		event_type: eventTypeFromPayload(row.payload_json),
		sig_valid: row.sig_valid,
		accepted: row.accepted,
		received_at: row.received_at,
		payload_json: row.payload_json,
	};
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function listEvents(limit = 50): Promise<ListEventsResponse> {
	const qs = new URLSearchParams();
	qs.set("limit", String(Math.max(1, Math.min(500, Math.floor(limit)))));
	const res = await fetch(`${BASE}/events?${qs.toString()}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	const raw = await parseOrThrow<RawListResponse>(res);
	return {
		count: raw.count,
		events: Array.isArray(raw.events) ? raw.events.map(normalizeEvent) : [],
	};
}

/**
 * Generate a fresh 32-byte hex signing secret client-side.
 * Returned once; caller is responsible for persisting or disposing.
 */
export function rotateSecret(): Promise<RotateSecretResult> {
	return new Promise((resolve) => {
		const bytes = new Uint8Array(32);
		if (typeof crypto !== "undefined" && crypto.getRandomValues) {
			crypto.getRandomValues(bytes);
		} else {
			// Deterministic fallback for non-browser/test envs (not security-critical here).
			for (let i = 0; i < bytes.length; i++) bytes[i] = (Math.random() * 256) | 0;
		}
		let hex = "";
		for (let i = 0; i < bytes.length; i++) {
			hex += (bytes[i] ?? 0).toString(16).padStart(2, "0");
		}
		resolve({ secret: hex, rotated_at: Math.floor(Date.now() / 1000) });
	});
}

// ---------------------------------------------------------------------------
// HMAC helpers (mirror the BE verifier so we can unit-test the contract)
// ---------------------------------------------------------------------------

function hexFromBytes(bytes: Uint8Array): string {
	let out = "";
	for (let i = 0; i < bytes.length; i++) {
		out += (bytes[i] ?? 0).toString(16).padStart(2, "0");
	}
	return out;
}

export async function hmacSha256Hex(
	secret: string,
	message: string,
): Promise<string> {
	const enc = new TextEncoder();
	const key = await crypto.subtle.importKey(
		"raw",
		enc.encode(secret),
		{ name: "HMAC", hash: "SHA-256" },
		false,
		["sign"],
	);
	const sig = await crypto.subtle.sign("HMAC", key, enc.encode(message));
	return hexFromBytes(new Uint8Array(sig));
}

/**
 * Build the exact signed string the BE will verify:
 * `${timestampSeconds}.${rawBody}`.
 */
export function buildSignaturePayload(opts: {
	timestampSec: number;
	rawBody: string;
}): string {
	return `${opts.timestampSec}.${opts.rawBody}`;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const bigcommerceIntegrationViaWebhookQueryKeys = {
	all: ["bigcommerce-integration-via-webhook"] as const,
	events: () =>
		[
			...bigcommerceIntegrationViaWebhookQueryKeys.all,
			"events",
		] as const,
	detail: (id: string) =>
		[...bigcommerceIntegrationViaWebhookQueryKeys.all, id] as const,
};
